import { useQuery } from "@tanstack/react-query";
import { environmentsApi, Environment } from "@/api/client";

interface EnvironmentSelectProps {
  value: string;
  onChange: (value: string) => void;
  className?: string;
  placeholder?: string;
  valueKey?: "id" | "slug"; // Which field to use as the option value
}

export default function EnvironmentSelect({
  value,
  onChange,
  className = "",
  placeholder = "All Environments",
  valueKey = "id",
}: EnvironmentSelectProps) {
  const { data: envsData } = useQuery({
    queryKey: ["environments"],
    queryFn: () => environmentsApi.list(),
  });

  const environments = envsData?.environments || [];

  // Group environments by their group
  const grouped = environments.reduce(
    (acc, env) => {
      const groupName = env.group_name || "Ungrouped";
      if (!acc[groupName]) {
        acc[groupName] = [];
      }
      acc[groupName].push(env);
      return acc;
    },
    {} as Record<string, Environment[]>,
  );

  // Sort group names, putting "Ungrouped" last
  const sortedGroupNames = Object.keys(grouped).sort((a, b) => {
    if (a === "Ungrouped") return 1;
    if (b === "Ungrouped") return -1;
    return a.localeCompare(b);
  });

  const getOptionValue = (env: Environment) =>
    valueKey === "slug" ? env.slug : env.id;

  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={`border border-gray-300 rounded-lg px-3 py-2 ${className}`}
    >
      <option value="">{placeholder}</option>
      {sortedGroupNames.map((groupName) => (
        <optgroup key={groupName} label={groupName}>
          {grouped[groupName]
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((env) => (
              <option key={env.id} value={getOptionValue(env)}>
                {env.name}
              </option>
            ))}
        </optgroup>
      ))}
    </select>
  );
}
